import React, { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import { characterAutoCompletePluginKey } from '../../extensions/CharacterAutoComplete';

interface CharacterSuggestionsProps {
  editor: Editor;
}

export function CharacterSuggestions({ editor }: CharacterSuggestionsProps) {
  const [state, setState] = useState<{
    active: boolean;
    suggestions: string[];
    selectedIndex: number;
    query: string;
    coords: { top: number; left: number } | null;
  }>({ active: false, suggestions: [], selectedIndex: 0, query: '', coords: null });

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const update = () => {
      const pluginState = characterAutoCompletePluginKey.getState(editor.state);
      if (!pluginState?.active || pluginState.suggestions.length === 0) {
        setState(prev => prev.active ? { ...prev, active: false } : prev);
        return;
      }

      // Get cursor position for floating menu
      const { from } = editor.state.selection;
      const coords = editor.view.coordsAtPos(from);

      setState({
        active: true,
        suggestions: pluginState.suggestions,
        selectedIndex: pluginState.selectedIndex,
        query: pluginState.query,
        coords: { top: coords.bottom + 4, left: coords.left },
      });
    };

    editor.on('transaction', update);
    return () => { editor.off('transaction', update); };
  }, [editor]);

  // Click outside to dismiss
  useEffect(() => {
    if (!state.active) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setState(prev => ({ ...prev, active: false }));
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [state.active]);

  if (!state.active || !state.coords) return null;

  // Determine if menu should flip above cursor
  const viewportHeight = window.innerHeight;
  const menuHeight = Math.min(state.suggestions.length, 8) * 32 + 8;
  const flipAbove = state.coords.top + menuHeight > viewportHeight - 40;

  const style: React.CSSProperties = {
    position: 'fixed',
    left: state.coords.left,
    ...(flipAbove
      ? { bottom: viewportHeight - state.coords.top + 8 }
      : { top: state.coords.top }),
    zIndex: 50,
  };

  const handleSelect = (name: string) => {
    const { $from } = editor.state.selection;
    const start = $from.start();
    const end = $from.end();
    const tr = editor.state.tr.insertText(name, start, end);
    editor.view.dispatch(tr);
    editor.view.focus();
  };

  return (
    <div
      ref={menuRef}
      style={style}
      className="bg-white dark:bg-ink-800 border border-ink-200 dark:border-ink-600 rounded-md shadow-lg py-1 min-w-[160px] max-w-[280px] animate-fade-in"
    >
      {state.suggestions.map((name, i) => {
        const queryLen = state.query.length;
        return (
          <button
            key={name}
            onMouseDown={(e) => {
              e.preventDefault();
              handleSelect(name);
            }}
            className={`w-full text-left px-3 py-1.5 text-sm font-mono tracking-wide transition-colors ${
              i === state.selectedIndex
                ? 'bg-blue-50 dark:bg-blue-900/30 text-ink-900 dark:text-ink-100'
                : 'text-ink-700 dark:text-ink-300 hover:bg-ink-50 dark:hover:bg-ink-700'
            }`}
          >
            <span className="font-semibold text-blue-600 dark:text-blue-400">{name.slice(0, queryLen)}</span>
            {name.slice(queryLen)}
          </button>
        );
      })}
    </div>
  );
}
